import React, { useState, useEffect, useMemo } from 'react';
import { useData } from '../App';
// Curriculum synonyms map: maps many textual curriculum labels to canonical tokens
const CURRICULUM_SYNONYMS: Record<string, string> = {
  'international baccalaureate': 'ib',
  'ib': 'ib',
  'ibdp': 'ib',
  'igcse': 'igcse',
  'cambridge': 'igcse',
  'cambridge igcse': 'igcse',
  'cbse': 'cbse',
  'central board of secondary education': 'cbse',
  'icse': 'icse',
  'isc': 'icse',
  'alternative': 'alternative',
  'state board': 'state',
  'all': 'all'
};

const normalizeCurricula = (arr: string[] | undefined) => {
  if (!Array.isArray(arr)) return [] as string[];
  const set = new Set<string>();
  for (const item of arr) {
    const key = (item || '').toString().trim().toLowerCase();
    set.add(CURRICULUM_SYNONYMS[key] || key);
  }
  return Array.from(set);
};
import { UserProfile, School } from '../types';
import { 
  Filter, Star, MapPin, Calendar, CheckCircle2, 
  MessageSquare, AlertCircle, Sparkles, BookOpen, Globe, 
  UserPlus, X, Heart, ArrowRightLeft, Trophy, 
  Quote, ChevronDown, ChevronUp, HelpCircle, SearchX
} from 'lucide-react';

interface SchoolsProps {
  userProfile: UserProfile;
}

const TRANSITION_TIPS: Record<string, { title: string; tip: string }> = {
  'US': {
    title: 'Transitioning from US 🇺🇸',
    tip: 'Math levels in Indian CBSE schools are generally 1-2 grades ahead of US public schools. IB/IGCSE align better. Consider getting a math tutor for the first 6 months.'
  },
  'UK': {
    title: 'Transitioning from UK 🇬🇧',
    tip: 'The academic year in India (June/April start) differs from UK (September). IB schools starting in August offer the smoothest calendar transition.'
  },
  'Germany': {
    title: 'Transitioning from Germany 🇩🇪',
    tip: 'Language can be a hurdle if coming from German-medium schools. Look for schools with strong ESL (English as Second Language) support programs.'
  },
  'Other': {
    title: 'General Relocation Tip',
    tip: 'Admissions for top schools often happen 6-8 months in advance (Oct/Nov). Secure admission before arriving if possible.'
  }
};

const GRADE_MAPPING: Record<string, { label: string; value: string; indian: string; age: string }[]> = {
  US: [
    { label: 'Pre-K (Age 4)', value: 'prek', indian: 'Junior KG', age: '4-5' },
    { label: 'Kindergarten (Age 5)', value: 'k', indian: 'Senior KG / Class 1', age: '5-6' },
    { label: 'Grade 1 (Age 6)', value: '1', indian: 'Class 1', age: '6-7' },
    { label: 'Grade 2 (Age 7)', value: '2', indian: 'Class 2', age: '7-8' },
    { label: 'Grade 3 (Age 8)', value: '3', indian: 'Class 3', age: '8-9' },
    { label: 'Grade 4 (Age 9)', value: '4', indian: 'Class 4', age: '9-10' },
    { label: 'Grade 5 (Age 10)', value: '5', indian: 'Class 5', age: '10-11' },
    { label: 'Grade 6 (Age 11)', value: '6', indian: 'Class 6', age: '11-12' },
    { label: 'Grade 7 (Age 12)', value: '7', indian: 'Class 7', age: '12-13' },
    { label: 'Grade 8 (Age 13)', value: '8', indian: 'Class 8', age: '13-14' },
    { label: 'Grade 9 (Age 14)', value: '9', indian: 'Class 9', age: '14-15' },
    { label: 'Grade 10 (Age 15)', value: '10', indian: 'Class 10', age: '15-16' },
    { label: 'Grade 11 (Age 16)', value: '11', indian: 'Class 11 (Junior College)', age: '16-17' },
    { label: 'Grade 12 (Age 17)', value: '12', indian: 'Class 12 (Junior College)', age: '17-18' },
  ],
  UK: [
    { label: 'Reception (Age 4)', value: 'rec', indian: 'Junior KG', age: '4-5' },
    { label: 'Year 1 (Age 5)', value: '1', indian: 'Senior KG / Class 1', age: '5-6' },
    { label: 'Year 2 (Age 6)', value: '2', indian: 'Class 1', age: '6-7' },
    { label: 'Year 3 (Age 7)', value: '3', indian: 'Class 2', age: '7-8' },
    { label: 'Year 4 (Age 8)', value: '4', indian: 'Class 3', age: '8-9' },
    { label: 'Year 5 (Age 9)', value: '5', indian: 'Class 4', age: '9-10' },
    { label: 'Year 6 (Age 10)', value: '6', indian: 'Class 5', age: '10-11' },
    { label: 'Year 7 (Age 11)', value: '7', indian: 'Class 6', age: '11-12' },
    { label: 'Year 8 (Age 12)', value: '8', indian: 'Class 7', age: '12-13' },
    { label: 'Year 9 (Age 13)', value: '9', indian: 'Class 8', age: '13-14' },
    { label: 'Year 10 (Age 14)', value: '10', indian: 'Class 9', age: '14-15' },
    { label: 'Year 11 (Age 15)', value: '11', indian: 'Class 10', age: '15-16' },
    { label: 'Year 12 (Age 16)', value: '12', indian: 'Class 11', age: '16-17' },
    { label: 'Year 13 (Age 17)', value: '13', indian: 'Class 12', age: '17-18' },
  ]
};

interface SchoolHelperProps {
  userProfile: UserProfile;
  moveMonth: string;
  setMoveMonth: (m: string) => void;
}

const SchoolHelper: React.FC<SchoolHelperProps> = ({ userProfile, moveMonth, setMoveMonth }) => {
  const [activeTab, setActiveTab] = useState<'grades' | 'timeline'>('grades');
  const [sourceSystem, setSourceSystem] = useState(userProfile?.sourceCountry === 'UK' ? 'UK' : 'US');
  const [selectedGrade, setSelectedGrade] = useState('');

  const gradeResult = useMemo(() => {
    if (!selectedGrade) return null;
    return GRADE_MAPPING[sourceSystem]?.find(g => g.value === selectedGrade);
  }, [selectedGrade, sourceSystem]);

  const timelineAdvice = useMemo(() => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const monthIndex = months.indexOf(moveMonth);
    
    // CBSE (April Start)
    let cbseStatus = '';
    if (monthIndex >= 0 && monthIndex <= 2) cbseStatus = 'Admission season! Apply now for April start.';
    else if (monthIndex === 3) cbseStatus = 'Session just started. Late admissions possible.';
    else if (monthIndex >= 4 && monthIndex <= 8) cbseStatus = 'Mid-year admission. Difficult in top schools.';
    else cbseStatus = 'End of term. Better to apply for next April.';

    // IB/IGCSE (August Start)
    let ibStatus = '';
    if (monthIndex >= 0 && monthIndex <= 5) ibStatus = 'Early application period. Ideal time.';
    else if (monthIndex === 6 || monthIndex === 7) ibStatus = 'Session starting! Immediate action needed.';
    else if (monthIndex >= 8 && monthIndex <= 11) ibStatus = 'Mid-semester. IB schools generally flexible.';
    
    return { cbse: cbseStatus, ib: ibStatus };
  }, [moveMonth]);

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden mb-8 animate-in fade-in slide-in-from-top-4">
      <div className="bg-indigo-900 text-white px-4 py-3 flex justify-between items-center">
        <h3 className="font-bold flex items-center gap-2 text-sm md:text-base">
          <HelpCircle size={18} className="text-indigo-300" /> 
          Indian School Decoder
        </h3>
        <div className="flex bg-indigo-800 rounded-lg p-1 text-xs font-bold">
          <button 
            onClick={() => setActiveTab('grades')}
            className={`px-3 py-1 rounded-md transition ${activeTab === 'grades' ? 'bg-white text-indigo-900 shadow' : 'text-indigo-200 hover:text-white'}`}
          >
            Grade Converter
          </button>
          <button 
            onClick={() => setActiveTab('timeline')}
            className={`px-3 py-1 rounded-md transition ${activeTab === 'timeline' ? 'bg-white text-indigo-900 shadow' : 'text-indigo-200 hover:text-white'}`}
          >
            Timelines
          </button>
        </div>
      </div>

      <div className="p-5">
        {activeTab === 'grades' ? (
          <div className="flex flex-col md:flex-row gap-8 items-center">
            <div className="flex-1 space-y-4 w-full">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Your Current System</label>
                <div className="flex gap-2">
                  <button onClick={() => {setSourceSystem('US'); setSelectedGrade('')}} className={`flex-1 py-2 rounded border font-bold text-sm ${sourceSystem === 'US' ? 'bg-indigo-50 border-indigo-200 text-indigo-700' : 'bg-white border-slate-200 text-slate-600'}`}>🇺🇸 USA</button>
                  <button onClick={() => {setSourceSystem('UK'); setSelectedGrade('')}} className={`flex-1 py-2 rounded border font-bold text-sm ${sourceSystem === 'UK' ? 'bg-indigo-50 border-indigo-200 text-indigo-700' : 'bg-white border-slate-200 text-slate-600'}`}>🇬🇧 UK</button>
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Current Grade / Year</label>
                <select 
                  className="w-full p-2.5 bg-white border border-slate-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-indigo-500 outline-none"
                  value={selectedGrade}
                  onChange={(e) => setSelectedGrade(e.target.value)}
                >
                  <option value="">Select Grade...</option>
                  {GRADE_MAPPING[sourceSystem]?.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="hidden md:block text-slate-300">
              <ArrowRightLeft size={32} />
            </div>

            <div className="flex-1 w-full bg-white p-4 rounded-xl border-2 border-dashed border-indigo-200 text-center min-h-[120px] flex flex-col justify-center items-center relative">
              {gradeResult ? (
                <>
                  <p className="text-xs text-slate-400 font-bold uppercase mb-1">Equivalent in India</p>
                  <p className="text-2xl font-bold text-indigo-600 mb-1">{gradeResult.indian}</p>
                  <p className="text-sm text-slate-500">Typical Age: {gradeResult.age} years</p>
                  <div className="absolute top-2 right-2">
                    <CheckCircle2 size={16} className="text-emerald-500" />
                  </div>
                </>
              ) : (
                <p className="text-sm text-slate-400 font-medium">Select a grade to see conversion</p>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
             <div className="flex items-center gap-4 bg-yellow-50 p-3 rounded-lg border border-yellow-100">
                <Calendar className="text-yellow-600 flex-shrink-0" size={20} />
                <div>
                  <label className="text-xs font-bold text-yellow-700 uppercase">When are you moving?</label>
                  <select 
                    value={moveMonth}
                    onChange={(e) => setMoveMonth(e.target.value)}
                    className="ml-2 bg-transparent font-bold text-slate-800 border-b border-yellow-300 focus:outline-none cursor-pointer"
                  >
                    {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map(m => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>
             </div>

             <div className="grid md:grid-cols-2 gap-4">
               <div className="bg-white p-4 rounded-lg border border-slate-200">
                 <div className="flex justify-between items-center mb-2">
                   <h4 className="font-bold text-slate-700">CBSE / ICSE Board</h4>
                   <span className="text-[10px] bg-slate-100 px-2 py-0.5 rounded text-slate-500 font-bold">STARTS APRIL</span>
                 </div>
                 <p className="text-sm text-slate-600">{timelineAdvice.cbse}</p>
                 <div className="mt-3 text-xs bg-slate-50 p-2 rounded text-slate-500">
                   <strong>Tip:</strong> Strict on age cutoff. Usually June/July cutoffs apply.
                 </div>
               </div>

               <div className="bg-white p-4 rounded-lg border border-slate-200">
                 <div className="flex justify-between items-center mb-2">
                   <h4 className="font-bold text-slate-700">IB / IGCSE Board</h4>
                   <span className="text-[10px] bg-indigo-100 px-2 py-0.5 rounded text-indigo-600 font-bold">STARTS AUGUST</span>
                 </div>
                 <p className="text-sm text-slate-600">{timelineAdvice.ib}</p>
                 <div className="mt-3 text-xs bg-slate-50 p-2 rounded text-slate-500">
                   <strong>Tip:</strong> Best aligned for US/UK/Europe moves. Look for August start badges.
                 </div>
               </div>
             </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Schools: React.FC<SchoolsProps> = ({ userProfile }) => {
  // Use the same data source as Dashboard for consistency
  const { schools } = useData();

  const [selectedCurricula, setSelectedCurricula] = useState<string[]>([]);
  const [compareList, setCompareList] = useState<string[]>([]);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [showHelper, setShowHelper] = useState(false);
  const [showAllAnyway, setShowAllAnyway] = useState(false);
  const [showInfoCard, setShowInfoCard] = useState(false);

  // Default to June as it's a common move month, but user can change it in Helper
  const [moveMonth, setMoveMonth] = useState<string>('June');

  // 1. FAMILY CHECK
  const hasKids = userProfile?.familyType === 'family_with_kids';

  // 2. CURRICULUM RECOMMENDATION LOGIC
  const recommendedCurricula = useMemo(() => {
    if (!userProfile) return ['All'];
    const country = userProfile.sourceCountry;
    if (country === 'US') return ['IB', 'IGCSE'];
    if (country === 'UK') return ['IGCSE', 'IB'];
    if (country === 'Germany') return ['IB'];
    return ['All'];
  }, [userProfile]);

  useEffect(() => {
    setSelectedCurricula(recommendedCurricula);
  }, [recommendedCurricula]);

  // 3. MATCH SCORE ALGORITHM
  const calculateMatchScore = (school: School) => {
    if (!userProfile) return 0;
    let score = 0;

    // A. Curriculum Match (+30) - normalize school curriculum before comparing
    const lowerRecommendedCurricula = recommendedCurricula.map(r => r.toLowerCase());
    const schoolCurrTokens = normalizeCurricula(school.curriculum);
    const hasRecommendedCurriculum = schoolCurrTokens.some(tok => lowerRecommendedCurricula.includes(tok) || lowerRecommendedCurricula.includes('all'));
    if (hasRecommendedCurriculum) score += 30;

    // B. Location Match (+20)
    if (userProfile.workLocation && userProfile.workLocation !== 'unsure' && userProfile.workLocation !== 'wfh') {
      const recommendedZones: Record<string, string[]> = {
        'hinjewadi': ['baner', 'balewadi', 'wakad', 'pimple_saudagar', 'aundh'],
        'kharadi': ['kharadi', 'viman_nagar', 'amanora', 'hadapsar', 'kalyani_nagar'],
        'magarpatta': ['amanora', 'hadapsar', 'kharadi', 'magarpatta', 'fatima_nagar']
      };
      const targetZones = recommendedZones[userProfile.workLocation] || [];
      if (school.nearbyLocalities.some(loc => targetZones.includes(loc))) {
        score += 20;
      }
    } else {
      score += 10; // Neutral score if no specific location or WFH
    }

    // C. NRI Friendly (+20)
    if (school.nriFriendly) score += 20;

    // D. Mid-year Admission (+15 if short timeline)
    if (userProfile.timeline === 'less_than_6_months' || userProfile.timeline === '6_to_12_months') {
      if (school.midYearAdmission) score += 15;
    } else {
      score += 15; // Not urgent, so give points anyway
    }

    // E. Sentiment (+15)
    if (school.chatSentiment === 'very_positive' || school.chatSentiment === 'positive') {
      score += 15;
    }

    // F. Timing Match (+10) - Smart Boost based on Move Month
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const mIndex = months.indexOf(moveMonth);
    
    // If moving May-August, boost August start schools
    if (mIndex >= 4 && mIndex <= 7 && school.academicYearStart === 'August') {
      score += 10;
    }
    // If moving Jan-April, boost April start schools
    if (mIndex >= 0 && mIndex <= 3 && school.academicYearStart === 'April') {
      score += 10;
    }

    return score;
  };

  // 4. SMART FILTERING & SORTING
  const processedSchools = useMemo(() => {
    let filtered = schools;

    // Filter by Curriculum using normalized tokens
    const lowerSelectedCurricula = selectedCurricula.map(s => s.toLowerCase());
    if (!lowerSelectedCurricula.includes('all')) {
      filtered = filtered.filter(school => {
        const tokens = normalizeCurricula(school.curriculum);
        return tokens.some(t => lowerSelectedCurricula.includes(t));
      });
    }

    // Sort by Match Score (Highest first)
    return filtered.map(s => ({ ...s, matchScore: calculateMatchScore(s) }))
                   .sort((a, b) => b.matchScore - a.matchScore);
  }, [schools, selectedCurricula, userProfile, recommendedCurricula, moveMonth]);

  // Handlers
  const toggleCurriculum = (curr: string) => {
    if (curr === 'All') {
      setSelectedCurricula(['All']);
      return;
    }
    let newSelection = selectedCurricula.filter(c => c !== 'All');
    if (newSelection.includes(curr)) {
      newSelection = newSelection.filter(c => c !== curr);
    } else {
      newSelection = [...newSelection, curr];
    }
    if (newSelection.length === 0) setSelectedCurricula(['All']);
    else setSelectedCurricula(newSelection);
  };

  const toggleCompare = (id: string) => {
    if (compareList.includes(id)) {
      setCompareList(compareList.filter(c => c !== id));
    } else {
      if (compareList.length < 4) setCompareList([...compareList, id]);
      else alert("You can compare up to 4 schools.");
    }
  };

  const renderComparisonModal = () => {
    const items = schools.filter(s => compareList.includes(s.id));

    return (
      <div 
        className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="compare-modal-title"
      >
        <div className="bg-white w-full max-w-6xl max-h-[90vh] overflow-hidden rounded-2xl shadow-2xl flex flex-col animate-in fade-in zoom-in-95 duration-200">
          <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
            <h3 id="compare-modal-title" className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <ArrowRightLeft size={20} className="text-indigo-600" /> Compare Schools
            </h3>
            <button 
              onClick={() => setShowCompareModal(false)} 
              className="p-2 hover:bg-slate-200 rounded-full transition"
              aria-label="Close comparison"
            >
              <X size={20} className="text-slate-500" />
            </button>
          </div>
          
          <div className="overflow-auto p-6 custom-scrollbar">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr>
                  <th className="p-3 border-b-2 border-slate-100 min-w-[150px] bg-slate-50">Feature</th>
                  {items.map(item => (
                    <th key={item.id} className="p-3 border-b-2 border-slate-100 min-w-[220px]">
                      <div className="text-lg font-bold text-slate-800">{item.name}</div>
                      <div className="text-xs font-normal text-slate-500 flex items-center gap-1">
                        <MapPin size={10} /> {item.location}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                <tr>
                  <td className="p-3 font-semibold text-slate-600 bg-slate-50">Match Score</td>
                  {items.map(item => {
                    const score = calculateMatchScore(item);
                    return (
                      <td key={item.id} className="p-3">
                         <div className="flex items-center gap-2">
                           <div className={`text-lg font-bold ${score >= 80 ? 'text-emerald-600' : 'text-indigo-600'}`}>
                             {score}%
                           </div>
                           {score >= 80 && <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-bold">BEST MATCH</span>}
                         </div>
                      </td>
                    );
                  })}
                </tr>
                <tr>
                  <td className="p-3 font-semibold text-slate-600 bg-slate-50">Curriculum</td>
                  {items.map(item => (
                    <td key={item.id} className="p-3">
                      <div className="flex flex-wrap gap-1">
                        {item.curriculum.map(c => (
                          <span key={c} className="text-xs bg-slate-100 px-2 py-1 rounded border border-slate-200 font-medium uppercase">{c}</span>
                        ))}
                      </div>
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="p-3 font-semibold text-slate-600 bg-slate-50">Grades</td>
                  {items.map(item => (
                    <td key={item.id} className="p-3 font-medium text-slate-700">{item.gradesOffered}</td>
                  ))}
                </tr>
                <tr>
                  <td className="p-3 font-semibold text-slate-600 bg-slate-50">Academic Year</td>
                  {items.map(item => (
                    <td key={item.id} className="p-3 font-medium text-slate-700">{item.academicYearStart}</td>
                  ))}
                </tr>
                <tr>
                  <td className="p-3 font-semibold text-slate-600 bg-slate-50">Annual Fee</td>
                  {items.map(item => (
                    <td key={item.id} className="p-3 font-bold text-slate-700">
                      ₹{(item.feeRangeAnnual.min/100000).toFixed(1)}L - {(item.feeRangeAnnual.max/100000).toFixed(1)}L
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="p-3 font-semibold text-slate-600 bg-slate-50">NRI Friendly</td>
                  {items.map(item => (
                    <td key={item.id} className="p-3">
                      {item.nriFriendly 
                        ? <span className="flex items-center gap-1 text-emerald-600 font-medium"><Globe size={14}/> Yes, very friendly</span> 
                        : <span className="text-slate-500">Standard</span>}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="p-3 font-semibold text-slate-600 bg-slate-50">Mid-Year Admission</td>
                  {items.map(item => (
                    <td key={item.id} className="p-3">
                      {item.midYearAdmission 
                        ? <span className="flex items-center gap-1 text-emerald-600 font-medium"><CheckCircle2 size={14}/> Possible</span> 
                        : <span className="flex items-center gap-1 text-red-500 font-medium"><X size={14}/> Difficult</span>}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="p-3 font-semibold text-slate-600 bg-slate-50 align-top pt-4">Sentiment</td>
                  {items.map(item => (
                    <td key={item.id} className="p-3 align-top pt-4">
                       <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold 
                         ${item.chatSentiment === 'very_positive' ? 'bg-emerald-100 text-emerald-700' : 
                           item.chatSentiment === 'positive' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'}`}>
                         {item.chatSentiment === 'very_positive' ? 'Highly Recommended' : 
                          item.chatSentiment === 'positive_but_expensive' ? 'Good but Pricey' : 
                          item.chatSentiment.replace('_', ' ')}
                       </span>
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="p-3 font-semibold text-slate-600 bg-slate-50 align-top">Highlights</td>
                  {items.map(item => (
                    <td key={item.id} className="p-3 align-top">
                      <ul className="space-y-1">
                        {item.highlights.slice(0, 3).map((h, i) => (
                          <li key={i} className="flex items-start gap-1.5 text-xs text-slate-600">
                            <CheckCircle2 size={12} className="mt-0.5 text-emerald-500 flex-shrink-0" /> {h}
                          </li>
                        ))}
                      </ul>
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="p-3 font-semibold text-slate-600 bg-slate-50 align-top">Concerns</td>
                  {items.map(item => (
                    <td key={item.id} className="p-3 align-top">
                      {item.concerns.length > 0 ? (
                        <ul className="space-y-1">
                          {item.concerns.map((c, i) => (
                            <li key={i} className="flex items-start gap-1.5 text-xs text-amber-700">
                              <AlertCircle size={12} className="mt-0.5 text-amber-500 flex-shrink-0" /> {c}
                            </li>
                          ))}
                        </ul>
                      ) : <span className="text-xs text-slate-400">None reported</span>}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // --- RENDER: NO KIDS VIEW ---
  if (userProfile && !hasKids && !showAllAnyway) {
    return (
      <div className="flex flex-col items-center justify-center h-[70vh] text-center p-6 animate-in fade-in">
        <div className="bg-slate-100 p-6 rounded-full mb-4">
          <BookOpen size={48} className="text-slate-400" />
        </div>
        <h2 className="text-2xl font-bold text-slate-800 mb-2">School Finder</h2>
        <p className="text-slate-500 max-w-md">
          Since your profile indicates you don't have children moving with you, we've hidden the school finder to keep things clean.
        </p>
        <div className="flex flex-col gap-3 mt-6">
          <button 
            onClick={() => setShowAllAnyway(true)}
            className="text-indigo-600 font-bold hover:bg-indigo-50 px-6 py-2 rounded-full transition"
          >
            Explore schools anyway
          </button>
          <button 
            onClick={() => window.location.reload()}
            className="text-slate-400 text-sm font-medium hover:text-slate-600"
          >
            Retake Quiz
          </button>
        </div>
      </div>
    );
  }

  const tipData = TRANSITION_TIPS[userProfile?.sourceCountry as keyof typeof TRANSITION_TIPS] || TRANSITION_TIPS['Other'];

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto pb-24 md:pb-6">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-6 gap-4 animate-in fade-in slide-in-from-top-4 duration-500">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            School Finder <Sparkles size={18} className="text-yellow-500" />
            <span className="text-xs font-medium px-2 py-1 bg-amber-100 text-amber-700 rounded-full">📍 Pune</span>
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Matched based on your profile: <span className="font-semibold text-slate-700">{userProfile?.sourceCountry} Curriculum</span>
          </p>
        </div>
      </div>

      {/* HELPER TOGGLE */}
      <div className="mb-6">
         <button 
           onClick={() => setShowHelper(!showHelper)}
           className="text-sm font-bold text-indigo-600 flex items-center gap-1 hover:text-indigo-800 transition"
           aria-expanded={showHelper}
         >
           {showHelper ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
           {showHelper ? 'Hide Indian Schools Guide' : 'Help me understand Indian schools'}
         </button>
         
         {showHelper && <SchoolHelper userProfile={userProfile} moveMonth={moveMonth} setMoveMonth={setMoveMonth} />}
      </div>

      {/* TRANSITION TIP CARD */}
      <div className="bg-indigo-900 text-white rounded-xl p-5 mb-8 shadow-lg flex items-start gap-4 animate-in fade-in">
        <div className="bg-white/10 p-2 rounded-lg">
          <Globe size={24} className="text-indigo-200" />
        </div>
        <div>
          <h3 className="font-bold text-indigo-100 text-sm uppercase tracking-wide mb-1">{tipData.title}</h3>
          <p className="text-sm md:text-base leading-relaxed text-white">
            {tipData.tip}
          </p>
        </div>
      </div>

      {/* QUICK INFO: Parents' Guide to Curricula (short bullets + links) - collapsed by default */}
      <div className="mb-4 flex items-center justify-between">
        <div className="text-sm text-slate-600">Quick Guide to Curricula — short summary for parents.</div>
        <button
          onClick={() => setShowInfoCard(!showInfoCard)}
          className="text-indigo-600 font-bold flex items-center gap-2 text-sm"
          aria-expanded={showInfoCard}
        >
          {showInfoCard ? (
            <>
              <ChevronUp size={16} /> Hide
            </>
          ) : (
            <>
              <ChevronDown size={16} /> Show
            </>
          )}
        </button>
      </div>

      {showInfoCard && (
        <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 mb-6 text-sm text-slate-700">
          <h3 className="text-lg font-bold text-slate-800 mb-2">Quick Guide to Curricula</h3>
          <div className="grid md:grid-cols-2 gap-2">
            <ul className="list-disc ml-5">
              <li><strong>IB</strong> — A skills-focused, inquiry-driven curriculum with strong international recognition. Fees tend to be higher; check whether the school offers strong CAS/extended-project support and how well it prepares students for university systems abroad. Visiting the school and asking about teacher qualifications and university counseling gives practical insight. <a className="text-indigo-600" href="https://www.ibo.org" target="_blank" rel="noreferrer">Official</a></li>
              <li><strong>IGCSE</strong> — Offers broad subject choice and a mix of coursework and exams, which works well for families likely to move internationally. Fees are typically mid–high; verify that the subjects you want (languages, sciences, arts) are offered and that the school supports transitions to A‑levels/IB or local boards. Ask about class sizes and language support if English isn’t your child’s first language. <a className="text-indigo-600" href="https://www.cambridgeinternational.org" target="_blank" rel="noreferrer">Official</a></li>
              <li><strong>CBSE</strong> — India’s national board, known for a structured, exam-focused syllabus and widespread school availability. It aligns well with competitive-engineering/medical exam preparation, but schools vary in teaching style and resources; check subject availability (e.g., computer science, foreign languages) and whether the school offers remedial or enrichment support for newcomers. Fees range widely—visit and ask current parents about classroom experience. <a className="text-indigo-600" href="https://www.cbse.gov.in" target="_blank" rel="noreferrer">Official</a></li>
              <li><strong>ICSE (CISCE)</strong> — Emphasizes English language skills and a detailed, concept-oriented syllabus that many parents find rigorous. Fees are usually mid→high; confirm how the school balances exam preparation with conceptual learning and whether they offer support for children switching from different systems. Speaking to teachers about assessment methods helps set expectations. <a className="text-indigo-600" href="https://cisce.org" target="_blank" rel="noreferrer">Official</a></li>
              <li><strong>State board</strong> — Managed by the state education department, these syllabi often reflect local language, context, and priorities. Quality, assessment style, and fees vary widely between states and individual schools, so check the language medium, subject coverage, and whether the school supports transfers or national-level exam preparation. For parents, practical checks matter most: ask if the school offers English-medium instruction or transitional support for children joining mid-year, what typical class sizes are, and whether they provide extra help for different curricula. Visiting the school and talking to current parents usually gives the clearest picture. <a className="text-indigo-600" href="https://education.gov.in" target="_blank" rel="noreferrer">Education Ministry</a></li>
            </ul>

            <ul className="list-disc ml-5">
              <li><strong>Fees (very rough)</strong> — Preschool/state: lower; CBSE/ICSE: mid; IGCSE/IB: highest.</li>
              <li><strong>Competitive exams</strong> — JEE/NEET need Class‑12 with required subjects; IB/IGCSE students can usually apply after subject/grade checks.</li>
              <li><strong>NRI notes</strong> — NRI quotas exist; many government roles need Indian citizenship.</li>
              <li><strong>Tip</strong> — Ask schools about subject coverage if your child plans Indian entrance exams.</li>
            </ul>
          </div>
        </div>
      )}

      {/* FILTERS TOOLBAR */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 mb-6 flex flex-col md:flex-row gap-6 items-start md:items-center sticky top-0 z-20">
        {/* Curriculum Filter */}
        <div className="flex-1">
          <label className="text-xs font-bold text-slate-400 uppercase mb-2 block">Curriculum Preference</label>
          <div className="flex flex-wrap gap-2">
            {['All', 'IB', 'IGCSE', 'CBSE', 'ICSE', 'State'].map(c => {
               const isActive = selectedCurricula.includes(c);
               const isRecommended = recommendedCurricula.includes(c);
               
               return (
                <button
                  key={c}
                  onClick={() => toggleCurriculum(c)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold transition flex items-center gap-1.5
                    ${isActive 
                      ? 'bg-indigo-600 text-white shadow-md ring-2 ring-indigo-100' 
                      : 'bg-slate-50 text-slate-600 hover:bg-slate-100 border border-slate-200'}`}
                  aria-pressed={isActive}
                >
                  {c}
                  {isRecommended && isActive && <Star size={10} className="text-yellow-300 fill-yellow-300" />}
                </button>
               );
            })}
          </div>
        </div>

      </div>

      {/* SCHOOLS TABLE */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="p-3 text-left">Name</th>
              <th className="p-3 text-left">Location</th>
              <th className="p-3 text-left">Grades</th>
              <th className="p-3 text-left">Curriculum</th>
              <th className="p-3 text-left">Session</th>
              <th className="p-3 text-left">Annual Fee</th>
              <th className="p-3 text-left">Compare</th>
            </tr>
          </thead>
          <tbody>
            {processedSchools.map(school => (
              <tr key={school.id} className="border-b hover:bg-slate-50">
                <td className="p-3 font-medium text-slate-800">{school.name}</td>
                <td className="p-3 text-slate-600">{school.location}</td>
                <td className="p-3 text-slate-600">{school.gradesOffered}</td>
                <td className="p-3 text-slate-600">{(school.curriculum || []).join(', ')}</td>
                <td className="p-3 text-slate-600">{school.academicYearStart}</td>
                <td className="p-3 text-slate-600">₹{school.feeRangeAnnual?.min ? (school.feeRangeAnnual.min/100000).toFixed(1) + 'L' : 'NA'} - {school.feeRangeAnnual?.max ? (school.feeRangeAnnual.max/100000).toFixed(1) + 'L' : 'NA'}</td>
                <td className="p-3">
                  <button onClick={() => toggleCompare(school.id)} className={`px-3 py-1 rounded text-xs font-bold ${compareList.includes(school.id) ? 'bg-indigo-600 text-white' : 'bg-white border border-slate-200'}`}>
                    {compareList.includes(school.id) ? 'Selected' : 'Compare'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {processedSchools.length === 0 && (
          <div className="p-6 text-center text-slate-500">No schools match your filters. Try selecting "All" curricula or turning off the location filter.</div>
        )}
      </div>

      {/* COMPARISON FLOATING BAR */}
      {compareList.length > 0 && (
        <div className="fixed bottom-20 md:bottom-8 left-1/2 transform -translate-x-1/2 bg-slate-900 text-white px-6 py-3 rounded-full shadow-xl z-40 flex items-center gap-4 animate-in slide-in-from-bottom-4 duration-300">
          <span className="font-medium text-sm">{compareList.length} schools selected</span>
          <div className="h-4 w-px bg-slate-700"></div>
          <button 
            onClick={() => setShowCompareModal(true)}
            className="flex items-center gap-2 font-bold text-sm text-indigo-300 hover:text-white transition"
          >
            Compare Now <ArrowRightLeft size={16} />
          </button>
          <button 
            onClick={() => setCompareList([])} 
            className="ml-2 hover:bg-slate-800 p-1 rounded-full transition"
            aria-label="Clear selection"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* COMPARISON MODAL */}
      {showCompareModal && renderComparisonModal()}
    </div>
  );
};

export default Schools;